<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  modelValue: {
    type: [Object, Array],
    default: () => ({}),
  },
  allowedModels: {
    type: Array,
    default: () => [],
  },
  dropdownMaxHeight: {
    type: String,
    default: 'max-h-80',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const store = useStore();

const attributes = computed(() =>
  (store.getters['attributes/getAttributes'] || []).filter(attr =>
    props.allowedModels.includes(attr.attribute_model)
  )
);

const normalizedParams = computed(() => {
  const value = props.modelValue;
  if (Array.isArray(value)) {
    const first = value[0];
    return typeof first === 'object' && first !== null ? first : {};
  }
  return typeof value === 'object' && value !== null ? value : {};
});

const selectedAttribute = computed(() =>
  attributes.value.find(
    attr => attr.attribute_key === normalizedParams.value.attribute_key
  )
);

const attributeOptions = computed(() =>
  attributes.value.map(attr => ({
    id: attr.attribute_key,
    name: attr.attribute_display_name,
  }))
);

const attributeSelectModel = computed({
  get: () =>
    selectedAttribute.value
      ? {
          id: selectedAttribute.value.attribute_key,
          name: selectedAttribute.value.attribute_display_name,
        }
      : null,
  set: option => {
    const key = option?.id ?? null;
    const definition = attributes.value.find(
      attr => attr.attribute_key === key
    );
    // Reset the value whenever the attribute changes; carry its model for the backend.
    emit('update:modelValue', [
      {
        attribute_key: key,
        attribute_model: definition?.attribute_model ?? null,
        value: '',
      },
    ]);
  },
});

const displayType = computed(
  () => selectedAttribute.value?.attribute_display_type
);

const isListType = computed(() => displayType.value === 'list');
const isCheckboxType = computed(() => displayType.value === 'checkbox');
const isSelectType = computed(() => isListType.value || isCheckboxType.value);

const valueInputType = computed(() => {
  if (displayType.value === 'date') return 'date';
  if (['number', 'currency', 'percent'].includes(displayType.value)) {
    return 'number';
  }
  return 'text';
});

const listOptions = computed(() =>
  (selectedAttribute.value?.attribute_values || []).map(item => ({
    id: item,
    name: item,
  }))
);

const booleanOptions = computed(() => [
  { id: 'true', name: t('AUTOMATION.ACTION.CUSTOM_ATTRIBUTE_TRUE') },
  { id: 'false', name: t('AUTOMATION.ACTION.CUSTOM_ATTRIBUTE_FALSE') },
]);

const valueModel = computed({
  get: () => normalizedParams.value.value ?? '',
  set: value =>
    emit('update:modelValue', [{ ...normalizedParams.value, value }]),
});

const valueSelectModel = computed({
  get: () => {
    const value = normalizedParams.value.value;
    if (value === undefined || value === null || value === '') return null;
    return { id: String(value), name: String(value) };
  },
  set: option => {
    let value = option?.id ?? '';
    if (isCheckboxType.value) value = value === 'true';
    emit('update:modelValue', [{ ...normalizedParams.value, value }]);
  },
});
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <SingleSelect
      v-model="attributeSelectModel"
      :options="attributeOptions"
      :placeholder="t('AUTOMATION.ACTION.CUSTOM_ATTRIBUTE_SELECT')"
      :dropdown-max-height="dropdownMaxHeight"
      disable-deselect
    />
    <template v-if="selectedAttribute">
      <SingleSelect
        v-if="isSelectType"
        v-model="valueSelectModel"
        :options="isCheckboxType ? booleanOptions : listOptions"
        :placeholder="t('AUTOMATION.ACTION.CUSTOM_ATTRIBUTE_VALUE')"
        :dropdown-max-height="dropdownMaxHeight"
        disable-search
      />
      <NextInput
        v-else
        v-model="valueModel"
        :type="valueInputType"
        size="sm"
        class="w-40"
        :placeholder="t('AUTOMATION.ACTION.CUSTOM_ATTRIBUTE_VALUE')"
      />
    </template>
  </div>
</template>
